import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, categories } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCat, setActiveCat] = useState(slug || 'all');

  const allCategories = [{ slug: 'all', name: '全部', icon: 'ri-apps-line' }, ...categories];

  const filteredVideos = useMemo(() => {
    if (activeCat === 'all') return videos;
    return videos.filter((v) => v.category === activeCat);
  }, [activeCat]);

  const currentCat = allCategories.find((c) => c.slug === activeCat);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: t('nav.category') },
              { label: currentCat?.name || '全部' },
            ]}
          />

          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {allCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => {
                  setActiveCat(cat.slug);
                  window.history.replaceState(null, '', `/category/${cat.slug}`);
                }}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCat === cat.slug
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-200/60 text-foreground-500 hover:text-foreground-800 hover:bg-background-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                {cat.name}
              </button>
            ))}
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
            {currentCat?.name || '全部'} - 共 {filteredVideos.length} 部影片
          </h1>

          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pb-14">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-foreground-400">
              <div className="w-16 h-16 flex items-center justify-center mb-4">
                <i className="ri-film-line text-4xl"></i>
              </div>
              <p className="text-lg">该分类暂无影片</p>
              <Link
                to="/category/all"
                className="mt-4 text-primary-500 hover:text-primary-400 text-sm cursor-pointer whitespace-nowrap"
              >
                查看全部影片
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}